import { createSelector } from "@ngrx/store";
import { AppState } from "src/app/shared/store/app.state";

import { InvalidOperationException } from "../../exceptions/exceptions";

import {
  ApplicationDocumentType,
  ApplicationFeatureState,
} from "./application.state";

export const selectFeatureState = (state: AppState) => state.application;

///////////////////////////////////////////////////////////////////////////////////////
// Single State Slice Selector
///////////////////////////////////////////////////////////////////////////////////////

export const selectIsBusy = createSelector(
  selectFeatureState,
  (state: ApplicationFeatureState) => state.isBusy,
);

export const selectApplicationId = createSelector(
  selectFeatureState,
  (state: ApplicationFeatureState) => state.applicationId,
);

export const selectExternalSignDocumentUrl = createSelector(
  selectFeatureState,
  (state: ApplicationFeatureState) => {
    const documents = state.applicationDocuments?.filter(
      (document) =>
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        document.applicationDocumentType ===
        ApplicationDocumentType.APPLICATION,
    );

    if (!documents) {
      throw new InvalidOperationException(
        `It should not be possible that we can not find a single document with the type '${ApplicationDocumentType.APPLICATION}'`,
      );
    }

    if (documents.length > 1) {
      throw new InvalidOperationException(
        `It should not be possible that we can find more than one document of a given type. Could find '${documents.length.toString()}' documents with type '${ApplicationDocumentType.APPLICATION}'`,
      );
    }

    const document = documents[0];

    if (!document) {
      throw new InvalidOperationException(
        "This exception should never occur as we already checked this with the exceptions above and was only added that the compiler can derive the correct type",
      );
    }

    if (!document.externalSignDocumentUrl) {
      throw new InvalidOperationException(
        "It should not be possible that we receive an application document without an external sign document url",
      );
    }

    return document.externalSignDocumentUrl;
  },
);

export const selectError = createSelector(
  selectFeatureState,
  (state: ApplicationFeatureState) => state.error,
);

///////////////////////////////////////////////////////////////////////////////////////
// Sub State Slice Selector
///////////////////////////////////////////////////////////////////////////////////////

export const selectHttpRequestState = createSelector(
  selectFeatureState,
  (state: ApplicationFeatureState) => {
    const httpRequestState = {
      isBusy: state.isBusy,
      error: state.error,
    };

    return httpRequestState;
  },
);
